import { parseSingpassError, VERIFY_RETURNED_KEY, type SingpassErrorCode } from "@kovernow/flows/kyc";
import { VERIFY_PATH } from "./safe-path";

/**
 * W-7: the SingPass return (`/account/verify?singpass_session=…` or
 * `?singpass_error=…`). The token is read and stripped from the URL before
 * anything else runs — before Sentry, the session restore and the auth
 * guard's `/sign-in?returnTo=` redirect — and is kept in memory only.
 * sessionStorage only gets a marker that a return happened (never the
 * token), so a reload or a sign-in detour shows "session ended".
 */
export interface SingpassReturn {
  token?: string;
  error?: SingpassErrorCode;
}

export interface SingpassReturnStore {
  /**
   * If `url` is the verify path carrying SingPass params: keep them and
   * return the URL without them; otherwise null (nothing to strip).
   */
  capture(url: URL): URL | null;
  /** The auth guard redirected to sign-in: the token can't be used (a different account may sign in). */
  dropToken(): void;
  /** The captured return, without consuming it (StrictMode-safe). */
  peek(): SingpassReturn | null;
  clear(): void;
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function markReturned(on: boolean): void {
  try {
    const s = safeSession();
    if (on) s?.setItem(VERIFY_RETURNED_KEY, "1");
    else s?.removeItem(VERIFY_RETURNED_KEY);
  } catch {
    // storage unavailable: a reload shows the start state instead.
  }
}

export function wasReturned(): boolean {
  try {
    return safeSession()?.getItem(VERIFY_RETURNED_KEY) === "1";
  } catch {
    return false;
  }
}

export function createSingpassReturnStore(): SingpassReturnStore {
  let current: SingpassReturn | null = null;
  return {
    capture(url) {
      if (url.pathname.replace(/\/+$/, "").toLowerCase() !== VERIFY_PATH) return null;
      const p = url.searchParams;
      if (!p.has("singpass_session") && !p.has("singpass_error")) return null;
      const token = p.get("singpass_session") || undefined;
      const error = parseSingpassError(p.get("singpass_error"));
      if (token || error) {
        current = { ...(token ? { token } : {}), ...(error ? { error } : {}) };
        // Only a token can be lost to a reload; an error return leaves no marker
        // to turn a later fresh visit into "session ended".
        if (token) markReturned(true);
      }
      const clean = new URL(url.href);
      clean.searchParams.delete("singpass_session");
      clean.searchParams.delete("singpass_error");
      return clean;
    },
    dropToken() {
      if (current?.token) {
        const { token: _dropped, ...rest } = current;
        current = Object.keys(rest).length ? rest : null;
      }
    },
    peek: () => current,
    clear() {
      current = null;
    },
  };
}

/** Page load: strip the SingPass params off `window.location` (replace, no new entry). */
export function captureFromLocation(store: SingpassReturnStore, w: Window = window): void {
  const clean = store.capture(new URL(w.location.href));
  if (clean) w.history.replaceState(w.history.state, "", clean.pathname + clean.search + clean.hash);
}
